import classNames from "classnames";
import CiroTable from "../shared/CiroTable/CiroTable";
import CiroTableHeader, {
  CiroTableHeaderAlignEnum,
} from "../shared/CiroTable/CiroTableHeader";
import { gql } from "@apollo/client";
import { AutopilotPreviewContact_AutopilotSessionSavedContactPreview } from "./AutopilotPreviewContact";
import CiroTableRow from "../shared/CiroTable/CiroTableRow";
import CiroTableCell, { CiroTableCellAlignEnum } from "../shared/CiroTable/CiroTableCell";
import CiroOrgContactCellInfo from "../shared/CiroTable/CiroOrgContactCellInfo";
import { CiroOrgContactCellInfo_OrgContact } from "../shared/CiroTable/CiroOrgContactCellInfo";
import { AutopilotPreviewTable_ContactMatchPreviewFragment } from "../../__generated__/graphql";
import AutopilotScoringResultTableCell from "../autopilotCuratedSearch/AutopilotScoringResultTableCell";
import AutopilotCriteriaMatchedCellInfo from "../automation/shared/AutopilotCriteriaMatchedCellInfo";
import LinkedInCompanyCellInfo, {
  LinkedInCompanyCellInfo_LinkedInCompany,
} from "../shared/CiroTable/LinkedInCompanyCellInfo";
import { useFragment as getFragmentData } from "../../__generated__";

export const AutopilotPreviewTable_ContactMatchPreview = gql`
  fragment AutopilotPreviewTable_ContactMatchPreview on AutopilotSessionPreviewResponse {
    contacts {
      ...AutopilotPreviewContact_AutopilotSessionSavedContactPreview
      rationale
      org_contact {
        id
        company_name
        ...CiroOrgContactCellInfo_OrgContact
        linkedInCompany {
          ...LinkedInCompanyCellInfo_LinkedInCompany
        }
      }
      scoringCriteriaResults {
        criterion_fit
        reasoning
        criterion_id
      }
    }
    autopilotCuratedSearch {
      scoringCriteria {
        id
        label
        criterion
      }
    }
  }
  ${LinkedInCompanyCellInfo_LinkedInCompany}
  ${AutopilotPreviewContact_AutopilotSessionSavedContactPreview}
  ${CiroOrgContactCellInfo_OrgContact}
`;

const AutopilotPreviewTable = ({
  contactsMatchPreview,
}: {
  contactsMatchPreview:
    | AutopilotPreviewTable_ContactMatchPreviewFragment
    | null
    | undefined;
}) => {
  
  if (!contactsMatchPreview || contactsMatchPreview.contacts.length === 0) {
    return (
      <div className={classNames("text-center", "text-neutral-600")}>
        No contacts found
      </div>
    );
  }

  const orderedContacts = contactsMatchPreview.contacts.sort((a, b) => {
    const getScorePriority = (contact: typeof a) => {
      const results = contact.scoringCriteriaResults || [];
      const trueCount = results.filter((r) => r.criterion_fit === true).length;
      const undefinedCount = results.filter(
        (r) => r.criterion_fit === undefined,
      ).length;
      const falseCount = results.filter(
        (r) => r.criterion_fit === false,
      ).length;
      // Return array of counts to compare in priority order
      return [trueCount, undefinedCount, falseCount];
    };

    const aScores = getScorePriority(a);
    const bScores = getScorePriority(b);

    // Compare each score type in priority order
    for (let i = 0; i < aScores.length; i++) {
      if (bScores[i] !== aScores[i]) {
        return bScores[i] - aScores[i];
      }
    }
    return 0;
  });
  return (
    <CiroTable scrollable={true}>
      <thead className={classNames("table-header-group")}>
        <CiroTableHeader isFirst={true} width="w-24">
          Name
        </CiroTableHeader>
        <CiroTableHeader>Company</CiroTableHeader>
        <CiroTableHeader align={CiroTableHeaderAlignEnum.CENTER}>Criteria matched</CiroTableHeader>
        {contactsMatchPreview.autopilotCuratedSearch.scoringCriteria.map(
          (criterion, idx) => (
            <CiroTableHeader
              className={classNames("hidden", "xl:table-cell")}
              key={criterion.id}
              align={CiroTableHeaderAlignEnum.CENTER}
              isLast={
                idx ===
                contactsMatchPreview.autopilotCuratedSearch.scoringCriteria
                  .length -
                  1
              }
            >
              {criterion.label}
            </CiroTableHeader>
          ),
        )}
      </thead>
      <tbody className={classNames("table-row-group")}>
        {orderedContacts.map((contactMatchPreview, idx) => {
          const isLast = idx === orderedContacts.length - 1;

          return (
            <CiroTableRow
              key={contactMatchPreview.org_contact.id}
              clickable={false}
            >
              <CiroTableCell isLastRow={isLast}>
                <CiroOrgContactCellInfo
                  orgContact={contactMatchPreview.org_contact}
                />
              </CiroTableCell>
              <CiroTableCell isLastRow={isLast}>
                <LinkedInCompanyCellInfo
                  linkedInCompany={
                    getFragmentData(
                      LinkedInCompanyCellInfo_LinkedInCompany,
                      contactMatchPreview.org_contact.linkedInCompany,
                    ) ?? null
                  }
                  companyName={
                    contactMatchPreview.org_contact.company_name ?? ""
                  }
                />
              </CiroTableCell>
              <CiroTableCell align={CiroTableCellAlignEnum.CENTER} isLastRow={isLast}>
                <AutopilotCriteriaMatchedCellInfo
                  scoringCriteria={
                    contactsMatchPreview.autopilotCuratedSearch.scoringCriteria
                  }
                  scoringCriteriaResults={(
                    contactMatchPreview.scoringCriteriaResults ?? []
                  ).map((result) => ({
                    matched_criteria: result.criterion_fit,
                    scoring_criterion_id: result.criterion_id,
                  }))}
                />
              </CiroTableCell>
              {contactsMatchPreview.autopilotCuratedSearch.scoringCriteria.map(
                (scoringCriterion) => {
                  const result =
                    contactMatchPreview.scoringCriteriaResults?.find(
                      (result) => result.criterion_id === scoringCriterion.id,
                    );
                  return (
                    <AutopilotScoringResultTableCell
                      changeBreakpointOnNavCollapse={false}
                      key={scoringCriterion.id}
                      result={result}
                      isLast={isLast}
                    />
                  );
                },
              )}
            </CiroTableRow>
          );
        })}
      </tbody>
    </CiroTable>
  );
};

export default AutopilotPreviewTable;
